import {
  ChartPopularIcon,
  ChatIcon,
  PersonIcon,
  StarIcon,
  AppsIcon,
  HomeIcon,
  ChartFunnelIcon,
  TeamIcon,
  CartSaleIcon,
  NotificationIcon,
} from '@shopify/polaris-icons';
import {
  NOTION_REPORT_TEMPLATE_ROLES,
  SALE_ROLES,
  STATS_ROLES,
  TEAM_ROLES,
  TRAFFIC_ROLES,
  USER_ROLES,
} from '../pages/User/constants/userRoles';

// Import loadable components
import Home from '../loadables/Home/Home';
import Apps from '../loadables/Apps/Apps';
import SupportAgent from '../loadables/SupportAgent/SupportAgent';
import HomeV2 from '../loadables/HomeV2/HomeV2';
import Crisp from '../loadables/Crisp/Crisp';
import Stats from '../loadables/Stats/Stats';
import User from '../loadables/User/User';
import SeoSuite from '../loadables/StatsFeature/SeoSuite/SeoSuite';
import Blog from '../loadables/StatsFeature/Blog/Blog';
import Marketing from '../loadables/Marketing/Marketing';
import Team from '../loadables/Team/Team';
import {seoOnLogo, seoSuiteLogo} from '../resources/images/logo';
import CEO from '../loadables/CEO/CEO';
import Sale from '../loadables/Sale/Sale';
import Detail from '../loadables/Sale/Detail/Detail';
import StatsFeature from '../loadables/StatsFeature/index';
import AppComparison from '../loadables/AppComparison/index';
import NotionTemplate from '../loadables/Stats/NotionTemplate/NotionTemplate';
import Notification from '../loadables/Notifications/Notifications';
import UpsertNotification from '../pages/Notifications/UpsertNotification';
/**
 * Route configuration with role-based protection
 * Each route can have:
 * - path: URL path
 * - component: React component to render
 * - exact: Whether to match path exactly
 * - label: Display name for navigation
 * - icon: Icon for navigation
 * - protection: Role protection configuration
 *   - requireAdmin: Boolean - requires admin access
 *   - requiredRoles: Array - required roles
 *   - requiredApps: Array - required app access
 *   - requireAnyRole: Boolean - whether any role is sufficient (default: false)
 * - showInNav: Boolean - whether to show in navigation (default: true)
 * - category: String - category for grouping in navigation
 */
export const ROUTE_CONFIG = [
  {
    path: '/home-old-version',
    component: Home,
    exact: true,
    label: 'Home',
    icon: HomeIcon,
    showInNav: false, // Don't show root path in nav
    protection: {},
  },
  {
    path: '/',
    component: HomeV2,
    exact: true,
    label: 'Reviews',
    icon: StarIcon,
    category: 'user',
    protection: {
      // Accessible to all authenticated users
    },
  },
  {
    path: '/apps',
    component: Apps,
    exact: true,
    label: 'Apps',
    icon: AppsIcon,
    category: 'user',
    showInNav: false, // Hidden from navigation
    protection: {},
  },
  {
    path: '/neo-ai',
    component: SupportAgent,
    exact: true,
    label: 'AI Support',
    icon: ChatIcon,
    category: 'user',
    showInNav: false, // Hidden from navigation
    protection: {},
  },
  {
    path: '/crisp',
    component: Crisp,
    exact: true,
    label: 'Crisp',
    icon: ChatIcon,
    category: 'user',
    protection: {},
  },
  {
    path: '/stats',
    component: Stats,
    exact: true,
    label: 'Stats',
    icon: ChartPopularIcon,
    category: 'user',
    protection: {
      requiredRoles: [STATS_ROLES.VIEW_STATS],
    },
  },
  {
    path: '/stats/notion-template',
    component: NotionTemplate,
    exact: true,
    label: 'Notion Template',
    icon: ChartPopularIcon,
    category: 'user',
    showInNav: false,
    protection: {
      requiredRoles: [NOTION_REPORT_TEMPLATE_ROLES.VIEW_TEMPLATE],
    },
  },
  {
    path: '/app-comparison',
    component: AppComparison,
    exact: true,
    label: 'App Comparison',
    icon: ChartPopularIcon,
    category: 'user',
    protection: {
      requiredRoles: [STATS_ROLES.VIEW_STATS],
    },
  },
  {
    path: '/users',
    component: User,
    exact: true,
    label: 'Users',
    icon: PersonIcon,
    category: 'user',
    protection: {
      requiredRoles: [USER_ROLES.VIEW_USER],
      //   requireAnyRole: true
    },
  },
  {
    path: '/teams',
    component: Team,
    exact: true,
    label: 'Teams',
    icon: TeamIcon,
    category: 'user',
    protection: {
      requiredRoles: [TEAM_ROLES.VIEW_TEAM],
      //   requireAnyRole: true
    },
  },
  {
    path: '/stats-feature/avada-seo-suite',
    component: SeoSuite,
    exact: true,
    label: 'SEO Suite',
    imageSrc: seoSuiteLogo,
    category: 'user',
    protection: {
      requiredApps: ['avada-seo-suite'],
    },
  },
  {
    path: '/stats-feature/seoon-blog',
    component: Blog,
    exact: true,
    label: 'SEOon Blog',
    imageSrc: seoOnLogo,
    category: 'user',
    protection: {
      requiredApps: ['seoon-blog'],
    },
  },
  {
    path: '/stats-feature/:appId',
    component: StatsFeature,
    exact: true,
    label: 'Stats Feature',
    category: 'user',
    showInNav: false,
    protection: {},
  },
  {
    path: '/marketing',
    component: Marketing,
    exact: true,
    label: 'Marketing',
    icon: ChartFunnelIcon,
    category: 'user',
    protection: {
      requiredRoles: [TRAFFIC_ROLES.VIEW_TRAFFIC],
    },
  },
  {
    path: '/ceo',
    component: CEO,
    exact: true,
    label: 'CEO',
    icon: PersonIcon,
    category: 'admin',
    protection: {
      requireAdmin: true,
    },
  },
  {
    path: '/sale',
    component: Sale,
    exact: true,
    label: 'Sale',
    icon: CartSaleIcon,
    category: 'user',
    protection: {
      requiredRoles: [SALE_ROLES.VIEW_POTENTIAL_CUSTOMERS],
    },
  },
  {
    path: '/sale/detail',
    component: Detail,
    exact: true,
    label: 'Sale Detail',
    icon: CartSaleIcon,
    category: 'user',
    showInNav: false,
    protection: {
      requiredRoles: [SALE_ROLES.VIEW_POTENTIAL_CUSTOMERS],
    },
  },
  {
    path: '/notification',
    component: Notification,
    exact: true,
    label: 'Notifications',
    icon: NotificationIcon,
    category: 'user',
    protection: {
      allowEmails: [
        'tunglv@avadagroup.com',
        'nghiavt@avada.email',
        'nghiavt@avadagroup.com',
        'linhnq@avadagroup.com',
        'dungtt@avadagroup.com',
      ],
    },
  },
  {
    path: '/notification/:action',
    component: UpsertNotification,
    exact: true,
    label: 'Create Notification',
    icon: NotificationIcon,
    category: 'user',
    showInNav: false,
    protection: {
      allowEmails: [
        'tunglv@avadagroup.com',
        'nghiavt@avada.email',
        'nghiavt@avadagroup.com',
        'linhnq@avadagroup.com',
        'dungtt@avadagroup.com',
      ],
    },
  },
];

/**
 * Get routes filtered by user permissions
 * @param {Object} userRole - User role context
 * @returns {Array} Filtered routes
 */
export function getAccessibleRoutes(userRole) {
  const {isAuthenticated, isAdmin, hasAnyRole, hasAllRoles, canAccessApp, user} = userRole;

  if (!isAuthenticated) {
    return [];
  }

  const email = user?.email;

  return ROUTE_CONFIG.filter((route) => {
    const protection = route.protection || {};

    if (protection.allowEmails && protection.allowEmails.length > 0) {
      if (email && !protection.allowEmails.includes(email)) {
        return false;
      }
    }

    // Check admin requirement
    if (protection.requireAdmin && !isAdmin) {
      return false;
    }

    // Check role requirements
    if (protection.requiredRoles && protection.requiredRoles.length > 0) {
      const hasRequiredRoles = protection.requireAnyRole
        ? hasAnyRole(protection.requiredRoles)
        : hasAllRoles(protection.requiredRoles);

      if (!hasRequiredRoles) {
        return false;
      }
    }

    // Check app access requirements
    if (protection.requiredApps && protection.requiredApps.length > 0) {
      const hasAppAccess = protection.requiredApps.some((appId) => canAccessApp(appId));
      if (!hasAppAccess) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Get navigation items filtered by user permissions
 * @param {Object} userRole - User role context
 * @returns {Array} Navigation items grouped by category
 */
export function getNavigationItems(userRole) {
  const accessibleRoutes = getAccessibleRoutes(userRole);

  // Filter routes that should show in navigation
  const navRoutes = accessibleRoutes.filter((route) => route.showInNav !== false);

  // Group by category
  const grouped = navRoutes.reduce((acc, route) => {
    const category = route.category || 'user';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push({
      label: route.label,
      to: route.path,
      icon: route?.icon,
      imageSrc: route?.imageSrc,
    });
    return acc;
  }, {});

  return grouped;
}

/**
 * Get a specific route configuration by path
 * @param {string} path - Route path
 * @returns {Object|null} Route configuration
 */
export function getRouteByPath(path) {
  return ROUTE_CONFIG.find((route) => route.path === path) || null;
}

/**
 * Check if user can access a specific route
 * @param {string} path - Route path
 * @param {Object} userRole - User role context
 * @returns {boolean} Whether user can access the route
 */
export function canAccessRoute(path, userRole) {
  const route = getRouteByPath(path);
  if (!route) return false;

  const accessibleRoutes = getAccessibleRoutes(userRole);
  return accessibleRoutes.some((r) => r.path === path);
}
